import React, { useEffect, useState, useRef, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, A11y } from "swiper/modules";
import { fetchMenuList, fetchBanners, fetchCafeList, fetchSoldOutItems } from "../api";
import { getBranchByCode } from "../indexedDBUtils";
import {
  trackBannerClick,
  trackCartFeatureAvailability,
  trackCartClosed,
  trackCartViewed,
  trackCartAbandoned,
  trackProductClick,
  trackCategoryClick,
  trackSearchUsage
} from "../utils/analytics";
import CategorySection from "./CategorySection";
import KioskMenu from "./KioskMenu";
import { isKioskMode, isOptionSetCategory, buildAllCategory } from "../config/kiosk";
import ProductDetailModal from "./ProductDetailModal";
import CustomizationChooserSheet from "./CustomizationChooserSheet";
import CartSheet from "./CartSheet";
import CartExpiryToast from "./CartExpiryToast";
import CartPage from "./CartPage";
import OrderSummary from "./OrderSummary";
import MaintenanceScreen from "./MaintenanceScreen";
import CartPageLegacy from "./CartPageLegacy";
import OrderSummaryLegacy from "./OrderSummaryLegacy";
import { useBilling } from "../context/BillingContext";
import CachedVideo from "./CachedVideo";
import CachedImage from "./CachedImage";
import {
  productHasProteinBreadChoice,
  PROTEIN_BREAD_FILTER_TAG,
  PROTEIN_BREAD_FILTER_TAG_ID,
} from "../utils/productBadges";
import "./MenuList.css";
import "swiper/css";
import "swiper/css/pagination";


const LOADING_PHRASES = [
  "Brewing your menu!",
  "Grinding fresh options!",
  "Prepping your favorites!",
  "Measuring the perfect blend!",
  "Filtering the selections!",
  "Tamping down the details!",
  "Extracting the flavors!",
  "Frothing up something special!",
];

const isVegTag = (tag) => {
  const name = tag?.name?.toLowerCase() || "";
  return name.includes("veg") && !name.includes("non");
};

const isNonVegTag = (tag) => {
  const name = tag?.name?.toLowerCase() || "";
  return name.includes("non") && name.includes("veg");
};

export default function MenuList({ selectedCafe, onBackToCafes, showCafeSelector = false, onClearCafe, cart = null }) {
  // The pay-from-the-menu + Circle rewards flow turns on only when BOTH the
  // ?allowBilling=true param (frontend) and the Lambda's billingEnable flag
  // (backend) agree. allowBilling is fixed per page load; billingEnable is
  // learned from the menu response below and pushed into the billing context.
  const { allowBilling, billingAllowed, setBillingEnable } = useBilling();
  const CartPageView = billingAllowed ? CartPage : CartPageLegacy;
  const OrderSummaryView = billingAllowed ? OrderSummary : OrderSummaryLegacy;

  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [productNames, setProductNames] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPhrase, setLoadingPhrase] = useState(0);
  // Backend maintenance-mode signal for this cafe (mirrors the mobile app). Only
  // acted on when ?allowBilling=true is also set — see the render gate below.
  const [maintenance, setMaintenance] = useState({ enabled: false, title: "", message: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isSearchSticky, setIsSearchSticky] = useState(false);
  const [isFilterSticky, setIsFilterSticky] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [banners, setBanners] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchClosing, setSearchClosing] = useState(false);
  const [searchPlaceholder, setSearchPlaceholder] = useState(0);
  const [loadedVideos, setLoadedVideos] = useState({});
  const [branchName, setBranchName] = useState(null);
  const [soldOutItems, setSoldOutItems] = useState({});
  const [isCartEnabled, setIsCartEnabled] = useState(false);
  const [showCartPage, setShowCartPage] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [customizationProduct, setCustomizationProduct] = useState(null);
  const [initialCartItem, setInitialCartItem] = useState(null);
  const [openMode, setOpenMode] = useState(null);
  const reopenCartAfterEdit = useRef(false);
  const clearSignalRef = useRef(cart?.clearSignal ?? 0);
  const sectionRefs = useRef({});
  const subcategoryRefs = useRef({});
  const searchBarRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchCloseTimeoutRef = useRef(null);
  const filterBarRef = useRef(null);
  const originalSearchPositionRef = useRef(0);
  const originalFilterPositionRef = useRef(0);

  // When the cart is cleared (from any source), also reset the menu filters.
  useEffect(() => {
    const sig = cart?.clearSignal ?? 0;
    if (sig !== clearSignalRef.current) {
      clearSignalRef.current = sig;
      setSelectedTags([]);
    }
  }, [cart?.clearSignal]);

  const handleAddToCart = useCallback((...args) => {
    if (cart) cart.addToCart(...args);
  }, [cart]);

  const handleDecreaseFromCart = useCallback((product) => {
    if (cart) cart.decreaseFromCart(product);
  }, [cart]);

  const getProductCartQty = useCallback((product) => {
    if (cart) return cart.getProductCartQty(product);
    return 0;
  }, [cart]);

  const getProductCartLineCount = useCallback((product) => {
    if (cart) return cart.getProductCartLineCount(product);
    return 0;
  }, [cart]);

  const setCartTrackingEnabled = cart?.setCartTrackingEnabled;

  const hasConfigurableOptions = useCallback((product) => {
    const variationGroups = Array.isArray(product?.variations) ? product.variations : [];
    const variantGroup = variationGroups.find(g => g?.isVariantGroup);
    if (variantGroup && (variantGroup.options?.length || 0) > 1) return true;
    const nonVariantGroups = variationGroups.filter(g => !g?.isVariantGroup);
    if (nonVariantGroups.some(g => (g?.options?.length || 0) > 0)) return true;
    if (variantGroup) {
      const hasNestedOptions = (variantGroup.options || []).some(
        opt => opt?.optionSets?.some(os => (os?.options?.length || 0) > 0)
      );
      if (hasNestedOptions) return true;
    }
    return false;
  }, []);

  // Generate dynamic search placeholder texts from actual product names
  const getSearchPlaceholders = () => {
    const defaultPlaceholders = ["Search menu items..."];

    if (productNames.length === 0) {
      return defaultPlaceholders;
    }

    // Shuffle and pick random product names
    const shuffled = [...productNames].sort(() => Math.random() - 0.5);
    const selectedProducts = shuffled.slice(0, 5); // Pick 5 random products

    const dynamicPlaceholders = selectedProducts.map(name => {
      // Remove anything in brackets ([], ())
      const sanitized = name.replace(/\[.*?\]|\(.*?\)/g, '').trim();
      return `Search for '${sanitized}'`;
    });

    return [...defaultPlaceholders, ...dynamicPlaceholders];
  };

  const searchPlaceholders = getSearchPlaceholders();

  // Highlight matched words in text
  const highlightText = (text, searchTerm) => {
    if (!text || !searchTerm || searchTerm.trim() === '') return text;

    const searchLower = searchTerm.toLowerCase().trim();
    const searchWords = searchLower.split(/\s+/).filter(word => word.length > 0);

    // Create a regex pattern that matches any of the search words (case insensitive)
    const pattern = searchWords.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');

    // Split text by matches and wrap matches in <strong> tags
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) => {
          // Check if this part matches any search word (case-insensitive)
          const isMatch = searchWords.some(word => part.toLowerCase() === word);
          // Only wrap matched text in strong tags, return others as plain strings
          return isMatch ? <strong key={index}>{part}</strong> : <React.Fragment key={index}>{part}</React.Fragment>;
        })}
      </>
    );
  };

  useEffect(() => {
    // Rotate loading phrases
    const phraseInterval = setInterval(() => {
      setLoadingPhrase((prev) => (prev + 1) % LOADING_PHRASES.length);
    }, 2000); // Change phrase every 2 seconds

    return () => clearInterval(phraseInterval);
  }, []);

  // Load branch name from IndexedDB when selectedCafe changes
  useEffect(() => {
    const loadBranchName = async () => {
      if (!selectedCafe) {
        setBranchName(null);
        return;
      }

      const branchCode = selectedCafe.ristaId || selectedCafe.cafeCode;
      if (!branchCode) {
        setBranchName(null);
        return;
      }

      try {
        const branch = await getBranchByCode(branchCode);
        if (branch) {
          setBranchName(branch.name);
        } else {
          // If branch not found, try to refresh the cache
          await fetchCafeList();
          const refreshedBranch = await getBranchByCode(branchCode);
          if (refreshedBranch) {
            setBranchName(refreshedBranch.name);
          }
        }
      } catch (error) {
        console.error('Error loading branch name:', error);
      }
    };

    loadBranchName();
  }, [selectedCafe]);

  useEffect(() => {
    setIsCartEnabled(false);
    setShowCartPage(false);
    setShowOrderSummary(false);
    setCustomizationProduct(null);
    setCartTrackingEnabled?.(false);

    // Track loading start time for minimum display duration
    const loadingStartTime = Date.now();
    const MIN_LOADING_TIME = 3000; // 3 seconds

    // Get cafeCode from URL query params first, then fall back to selectedCafe
    const urlParams = new URLSearchParams(window.location.search);
    const urlCafeCode = urlParams.get('cafeCode');
    const cafeCode = urlCafeCode || selectedCafe?.ristaId || "0";

    fetchMenuList(cafeCode).then((data) => {
      const loadedCategories = data.categories || [];
      const loadedTags = data.tags || [];
      const hasProteinBreadProducts = loadedCategories.some(cat =>
        cat.products?.some(productHasProteinBreadChoice)
      );
      const filterTags = hasProteinBreadProducts
        ? [...loadedTags, PROTEIN_BREAD_FILTER_TAG]
        : loadedTags;

      setCategories(loadedCategories);
      setTags(filterTags);
      setSelectedTags(prev => prev.filter(id => filterTags.some(tag => tag.id === id)));
      setProductNames(data.productNames || []);
      setStore(data.store || null);
      // Backend maintenance mode for this cafe. The render gate below only shows
      // the maintenance screen when ?allowBilling=true is also set.
      setMaintenance(data.maintenance || { enabled: false, title: "", message: "" });
      // Publish the backend billingEnable flag so the billing context can AND
      // it with ?allowBilling and light up the pay + rewards flow.
      const backendBillingEnable = Boolean(data.billingEnable);
      setBillingEnable(backendBillingEnable);
      // Add-to-cart is available when the cafe's Rista cart flag is on, or when
      // the full billing flow is allowed (both gates) — kiosk included.
      const cartEnabled = Boolean(data.isCartEnabled) || (allowBilling && backendBillingEnable);
      setIsCartEnabled(cartEnabled);
      setCartTrackingEnabled?.(cartEnabled);

      if (cartEnabled) {
        trackCartFeatureAvailability({
          cafeId: cafeCode,
          cafeName: data.store?.name || branchName || selectedCafe?.name,
          isCartEnabled: cartEnabled,
        });
      }

      if (cart?.setTaxRates) {
        const taxMap = {};
        (data.taxTypes || []).forEach((taxType) => {
          if (taxType?.taxTypeId) {
            taxMap[taxType.taxTypeId] = {
              name: taxType.name || taxType.taxName || taxType.title || "Tax",
              percentage: Number(taxType.percentage || 0),
            };
          }
        });
        cart.setTaxRates(taxMap);
      }
      // A cart saved before today's menu prices changed would be refused at
      // checkout ("price changed") forever — bring its prices up to date.
      if (cartEnabled) cart?.repriceCartFromMenu?.(data.priceIndex);

      // Fetch sold-out items after menu is loaded
      const branchCode = urlCafeCode || selectedCafe?.ristaId || "CA-096";
      fetchSoldOutItems(branchCode).then((soldOutMap) => {
        setSoldOutItems(soldOutMap);
      });

      // Calculate elapsed time and wait if needed
      const elapsedTime = Date.now() - loadingStartTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

      setTimeout(() => {
        setLoading(false);
      }, remainingTime);
    }).catch((error) => {
      console.error("Error loading menu:", error);

      // Even on error, respect minimum loading time
      const elapsedTime = Date.now() - loadingStartTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

      setTimeout(() => {
        setLoading(false);
      }, remainingTime);
    });

    const handleScroll = () => {
      if (!searchMode) {
        setShowScrollTop(window.scrollY > 300);

        if (searchBarRef.current && originalSearchPositionRef.current === 0) {
          originalSearchPositionRef.current =
            searchBarRef.current.getBoundingClientRect().top + window.scrollY;
        }

        if (originalSearchPositionRef.current > 0) {
          setIsSearchSticky(window.scrollY >= originalSearchPositionRef.current);
        }

        // Handle filter bar stickiness
        if (filterBarRef.current && originalFilterPositionRef.current === 0) {
          originalFilterPositionRef.current =
            filterBarRef.current.getBoundingClientRect().top + window.scrollY;
        }

        if (originalFilterPositionRef.current > 0) {
          // Make filter sticky when search is sticky and filter scrolls to top
          const searchHeight = isSearchSticky ? (searchBarRef.current?.offsetHeight || 0) : 0;
          setIsFilterSticky(window.scrollY >= originalFilterPositionRef.current - searchHeight);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);

    setTimeout(() => {
      if (searchBarRef.current) {
        originalSearchPositionRef.current =
          searchBarRef.current.getBoundingClientRect().top + window.scrollY;
      }
      if (filterBarRef.current) {
        originalFilterPositionRef.current =
          filterBarRef.current.getBoundingClientRect().top + window.scrollY;
      }
    }, 100);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [searchMode, selectedCafe, setCartTrackingEnabled]); // Add selectedCafe as dependency

  useEffect(() => {
    let isActive = true;

    const loadBanners = async () => {
      // Use store.id from the menu response, or fall back to selectedCafe
      const cafeCode = selectedCafe?.ristaId || selectedCafe?.cafeCode || "0";

      try {
        const fetchedBanners = await fetchBanners({
          cafeCode,
        });

        if (!isActive) {
          return;
        }

        const validBanners = (fetchedBanners || []).filter(
          (banner) => banner.mediaUrl && banner.mediaType !== "unknown"
        );

        setBanners(validBanners);

        // Extract video URLs for prefetching
        const videoUrls = validBanners
          .filter(banner => banner.mediaType === "video")
          .map(banner => banner.mediaUrl);

        // Extract image URLs for prefetching
        const imageUrls = validBanners
          .filter(banner => banner.mediaType === "image")
          .map(banner => banner.mediaUrl);

        // Prefetch videos in the background
        if (videoUrls.length > 0) {
          import("../utils/videoCacheService").then(({ default: videoCacheService }) => {
            videoCacheService.init().then(() => {
              videoCacheService.prefetchVideos(videoUrls);
            });
          });
        }

        // Prefetch images in the background
        if (imageUrls.length > 0) {
          import("../utils/imageCacheService").then(({ default: imageCacheService }) => {
            imageCacheService.init().then(() => {
              imageCacheService.prefetchImages(imageUrls);
            });
          });
        }
      } catch (error) {
        if (isActive) {
          setBanners([]);
        }
      }
    };

    loadBanners();

    return () => {
      isActive = false;
    };
  }, [store, selectedCafe]);

  // Animate search placeholder text
  useEffect(() => {
    if (searchPlaceholders.length === 0) return;

    const interval = setInterval(() => {
      setSearchPlaceholder((prev) => (prev + 1) % searchPlaceholders.length);
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [searchPlaceholders.length, productNames.length]);

  // Track search usage with debounce
  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length < 4) return;

    const timeout = setTimeout(() => {
      trackSearchUsage(
        selectedCafe?.ristaId || selectedCafe?.cafeCode,
        branchName || selectedCafe?.name,
        searchTerm.trim()
      );
    }, 1500);

    return () => clearTimeout(timeout);
  }, [searchTerm, selectedCafe, branchName]);

  // Swiper handles banner autoplay and looping.

  const scrollToCategory = (id, subcategoryName = null, isRetry = false) => {
    // Track category click only on first call (not retry)
    if (!isRetry) {
      const category = categories.find(cat => cat.id === id);
      trackCategoryClick(
        selectedCafe?.ristaId || selectedCafe?.cafeCode,
        branchName || selectedCafe?.name,
        { id, name: category?.parentName || subcategoryName }
      );
    }

    // If subcategoryName is provided, try to scroll to that subcategory
    let element = null;
    if (subcategoryName) {
      const subcategoryKey = `${id}-${subcategoryName}`;
      element = subcategoryRefs.current[subcategoryKey];
    }

    // Fall back to category if subcategory not found
    if (!element) {
      element = sectionRefs.current[id];
    }

    if (element) {
      // Always account for both sticky bars if filter exists
      // Categories are always below the filter bar, so both will be sticky at destination
      const searchHeight = searchBarRef.current?.offsetHeight || 0;
      const filterHeight = filterBarRef.current?.offsetHeight || 0;

      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const offsetTop = elementTop - searchHeight - filterHeight - 10;

      window.scrollTo({ top: offsetTop, behavior: "smooth" });

      // Retry once after layout has settled to correct position
      if (!isRetry) {
        setTimeout(() => {
          scrollToCategory(id, subcategoryName, true);
        }, 400);
      }
    }
    setMobileMenuOpen(false);
  };

  const handleProductClick = (product, meta) => {
    trackProductClick(
      selectedCafe?.ristaId || selectedCafe?.cafeCode,
      branchName || selectedCafe?.name,
      { id: product.skuCode, name: product.name, category: product.subCategoryName }
    );

    const source = meta?.source;
    if (isCartEnabled && (source === 'add' || source === 'customization') && hasConfigurableOptions(product)) {
      const productQty = getProductCartQty(product);
      if (productQty > 0) {
        setCustomizationProduct(product);
        return;
      }
    }

    setOpenMode(isCartEnabled && source === 'add' ? 'add' : null);
    setInitialCartItem(null);
    setSelectedProduct(product);
  };

  const handleAddNewCustomization = useCallback(() => {
    const product = customizationProduct;
    setCustomizationProduct(null);
    setOpenMode('add');
    setInitialCartItem(null);
    setSelectedProduct(product);
  }, [customizationProduct]);

  const handleEditCustomization = useCallback((cartItem) => {
    const product = customizationProduct;
    setCustomizationProduct(null);
    setOpenMode('add');
    setInitialCartItem(cartItem);
    setSelectedProduct(product);
  }, [customizationProduct]);

  const handleEditCartItem = useCallback((cartItem) => {
    let foundProduct = null;
    for (const cat of categories) {
      foundProduct = cat.products.find(p =>
        (p.id || p.skuCode || p.name) === cartItem.productId
      );
      if (foundProduct) break;
    }
    if (foundProduct) {
      reopenCartAfterEdit.current = true;
      setOpenMode('add');
      setInitialCartItem(cartItem);
      setSelectedProduct(foundProduct);
    }
  }, [categories]);
  const closeModal = () => {
    const shouldReopenCart = reopenCartAfterEdit.current;
    reopenCartAfterEdit.current = false;
    setSelectedProduct(null);
    setOpenMode(null);
    setInitialCartItem(null);
    if (shouldReopenCart) {
      setTimeout(() => setShowCartPage(true), 100);
    }
  };
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const handleSearchClick = () => {
    setSearchMode(true);
  };

  const handleBackFromSearch = useCallback(() => {
    if (searchClosing) return;
    setSearchClosing(true);
    searchCloseTimeoutRef.current = setTimeout(() => {
      setSearchMode(false);
      setSearchTerm("");
      setSearchClosing(false);
    }, 300); // Match animation duration
  }, [searchClosing]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!searchMode) return undefined;

    const handleSearchKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleBackFromSearch();
      }
    };

    window.addEventListener("keydown", handleSearchKeyDown);
    return () => window.removeEventListener("keydown", handleSearchKeyDown);
  }, [searchMode, handleBackFromSearch]);

  useEffect(() => () => {
    if (searchCloseTimeoutRef.current) {
      clearTimeout(searchCloseTimeoutRef.current);
    }
  }, []);

  // Handle back to cafe selection
  const handleBackToCafes = () => {
    if (onBackToCafes) {
      onBackToCafes();
    }
  };

  // Toggle accordion for categories
  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Handle video loaded
  const handleVideoLoaded = (bannerId) => {
    // Temporarily disabled for testing
    setLoadedVideos((prev) => ({
      ...prev,
      [bannerId]: true,
    }));
  };

  // Handle banner click
  const handleBannerClick = (banner) => {
    if (!banner.redirectMode) return;

    trackBannerClick(
      selectedCafe?.ristaId || selectedCafe?.cafeCode,
      branchName || selectedCafe?.name,
      { id: banner.id, name: banner.name || banner.title }
    );

    const redirectMode = banner.redirectMode.toLowerCase();

    if (redirectMode === 'category') {
      // Scroll to category by matching category name (temporary fix for ID mismatch)
      if (banner.category && banner.category.categoryName) {
        const categoryName = banner.category.categoryName.toLowerCase().trim();
        // Find the category by name
        const foundCategory = categories.find(cat =>
          cat.parentName?.toLowerCase().trim() === categoryName
        );
        if (foundCategory) {
          const subcategoryName = banner.subCategory?.categoryName || null;
          scrollToCategory(foundCategory.id, subcategoryName);
        }
      }
    } else if (redirectMode === 'product') {
      // Open product modal using ristaSkuCode
      if (banner.product && banner.product.variations && banner.product.variations.length > 0) {
        const ristaSkuCode = banner.product.variations[0]?.masterProduct?.ristaSkuCode;
        if (ristaSkuCode) {
          // Find the product in categories by matching skuCode
          let foundProduct = null;
          for (const category of categories) {
            foundProduct = category.products.find(p => p.skuCode === ristaSkuCode);
            if (foundProduct) break;
          }
          if (foundProduct) {
            setSelectedProduct(foundProduct);
          }
        }
      }
    }
  };

  // Handle tag selection toggle with veg/non-veg mutual exclusivity
  // Helper function to check if a product should be hidden due to sold-out variants
  const isProductSoldOut = (product, soldOutItems) => {
    if (!soldOutItems || Object.keys(soldOutItems).length === 0) {
      return false;
    }

    // First check if the product itself is sold out
    if (product.skuCode && product.skuCode in soldOutItems) {
      return true;
    }

    // Check if product has variants
    const variantGroup = product.variations?.find(v => v.isVariantGroup);
    if (!variantGroup || !variantGroup.options || variantGroup.options.length === 0) {
      // No variants, only check product-level skuCode (already checked above)
      return false;
    }

    // If there's only one variant and it's sold out, hide the product
    if (variantGroup.options.length === 1) {
      const singleVariant = variantGroup.options[0];
      if (singleVariant.skuCode && singleVariant.skuCode in soldOutItems) {
        return true;
      }
    }

    // If there are multiple variants, check if ALL are sold out
    if (variantGroup.options.length > 1) {
      const allVariantsSoldOut = variantGroup.options.every(option => 
        option.skuCode && option.skuCode in soldOutItems
      );
      if (allVariantsSoldOut) {
        return true;
      }
    }

    return false;
  };

  const handleTagToggle = (tagId) => {
    setSelectedTags((prev) => {
      // Find the clicked tag
      const clickedTag = tags.find(t => t.id === tagId);
      const clickedTagName = clickedTag?.name?.toLowerCase() || '';

      // Check if this is a veg or non-veg tag
      const isVegTag = clickedTagName.includes('veg') && !clickedTagName.includes('non');
      const isNonVegTag = clickedTagName.includes('non') && clickedTagName.includes('veg');

      // If clicking veg or non-veg, find and remove the opposite one
      if (isVegTag || isNonVegTag) {
        // Find the opposite tag's ID
        const oppositeTag = tags.find(t => {
          const tagName = t.name?.toLowerCase() || '';
          if (isVegTag) {
            return tagName.includes('non') && tagName.includes('veg');
          } else {
            return tagName.includes('veg') && !tagName.includes('non');
          }
        });

        // Remove opposite tag if it exists in selected tags
        const withoutOpposite = oppositeTag
          ? prev.filter((id) => id !== oppositeTag.id)
          : prev;

        // Toggle the clicked tag
        const newTags = withoutOpposite.includes(tagId)
          ? withoutOpposite.filter((id) => id !== tagId)
          : [...withoutOpposite, tagId];

        return newTags;
      }

      // For other tags, just toggle normally
      const newTags = prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId];
      return newTags;
    });
  };

  const productMatchesTagFilter = (product, tagId) => {
    if (tagId === PROTEIN_BREAD_FILTER_TAG_ID) {
      return productHasProteinBreadChoice(product);
    }

    // Check if product directly has this tag
    if (product.tags?.some((tag) => tag.id === tagId)) {
      return true;
    }

    // If the selected tag is a non-veg tag, also match products that have an egg tag
    // (egg items should show under the non-veg filter even if they don't have a non-veg tag)
    const selectedTag = tags.find(t => t.id === tagId);
    const isSelectedNonVeg = selectedTag &&
      (selectedTag.name?.toLowerCase().includes('non') && selectedTag.name?.toLowerCase().includes('veg'));

    if (isSelectedNonVeg) {
      return product.tags?.some((tag) =>
        tag?.name?.toLowerCase().includes('egg')
      );
    }

    return false;
  };

  const productMatchesTagFilters = (product, activeTagFilters) => (
    activeTagFilters.length === 0 ||
    activeTagFilters.some((tagId) => productMatchesTagFilter(product, tagId))
  );

  const vegTag = tags.find(isVegTag);
  const nonVegTagDef = tags.find(isNonVegTag);
  const hasNonVegProducts = nonVegTagDef && categories.some(cat =>
    cat.products?.some(p =>
      p.tags?.some(tag => tag.id === nonVegTagDef.id || tag?.name?.toLowerCase().includes('egg'))
    )
  );
  const nonVegTag = hasNonVegProducts ? nonVegTagDef : null;
  const activeDietTag = [vegTag, nonVegTag].find((tag) => tag && selectedTags.includes(tag.id));
  const activeDietName = activeDietTag?.name || "";

  const filteredCategories = categories
    .map((cat) => {
      const searchLower = searchTerm.toLowerCase().trim();
      const activeTagFilters = selectedTags;

      // If search is empty, return all products (with tag filter only)
      if (!searchLower) {
        const filteredProducts = cat.products.filter((p) => {
          // Filter out sold-out items (including products with all variants sold out)
          const isNotSoldOut = !isProductSoldOut(p, soldOutItems);
          const matchesTags = productMatchesTagFilters(p, activeTagFilters);
          return isNotSoldOut && matchesTags;
        });

        // Filter subcategories to only include those with at least one product
        const subcategoriesWithProducts = cat.subCategories?.filter(subCat =>
          filteredProducts.some(p => p.subCategoryName === subCat.name)
        ) || [];

        return {
          ...cat,
          products: filteredProducts,
          subCategories: subcategoriesWithProducts,
        };
      }

      // Split search into words
      const searchWords = searchLower.split(/\s+/).filter(word => word.length > 0);

      // Helper function: ALL search words must be found in the text
      const fuzzyMatch = (text) => {
        if (!text) return false;
        const textLower = text.toLowerCase();
        // Every search word must be found somewhere in the text
        return searchWords.every(word => textLower.includes(word));
      };

      // Check if category name matches search
      const categoryMatches = fuzzyMatch(cat.parentName);

      // Get list of matching subcategory names
      const matchingSubcategories = cat.subCategories?.filter(subCat =>
        fuzzyMatch(subCat.name)
      ).map(subCat => subCat.name) || [];

      const hasSubcategoryMatch = matchingSubcategories.length > 0;

      // If category or subcategory matches, include all products (with tag filter)
      // Otherwise, filter products by name
      const filteredProducts = cat.products.filter((p) => {
        // Filter out sold-out items (including products with all variants sold out)
        const isNotSoldOut = !isProductSoldOut(p, soldOutItems);
        // Tag filtering: if any tags are selected, product must have at least one of them
        const matchesTags = productMatchesTagFilters(p, activeTagFilters);

        // If category name matches, show all products in category
        if (categoryMatches) {
          return isNotSoldOut && matchesTags;
        }

        // If subcategory matches, only show products from that subcategory
        if (hasSubcategoryMatch) {
          const productInMatchingSubcategory = matchingSubcategories.includes(p.subCategoryName);
          return isNotSoldOut && productInMatchingSubcategory && matchesTags;
        }

        // Otherwise, check product name OR description with fuzzy matching AND tags
        const productNameMatches = fuzzyMatch(p.name);
        const descriptionMatches = fuzzyMatch(p.description || p.desc || '');

        return isNotSoldOut && (productNameMatches || descriptionMatches) && matchesTags;
      });

      // Filter subcategories to only include those with at least one product
      const subcategoriesWithProducts = cat.subCategories?.filter(subCat =>
        filteredProducts.some(p => p.subCategoryName === subCat.name)
      ) || [];

      return {
        ...cat,
        products: filteredProducts,
        subCategories: subcategoriesWithProducts,
      };
    })
    .filter((cat) => cat.products.length > 0);

  const trimmedSearchTerm = searchTerm.trim();
  const searchResultCount = trimmedSearchTerm
    ? filteredCategories.reduce((total, category) => total + category.products.length, 0)
    : 0;
  const searchSuggestions = categories
    .filter((category) => !isOptionSetCategory(category) && category.parentName)
    .slice(0, 5);

  // Kiosk (portrait tablet) mode: opt-in via ?mode=kiosk.
  const kioskMode = isKioskMode();

  // Sidebar navigation + normal browsing: filtered by diet/stock only, never
  // by the search text — the rail should stay put while the guest types.
  const kioskRailCategories = kioskMode
    ? categories
        .map((cat) => {
          const filteredProducts = cat.products.filter((p) =>
            !isProductSoldOut(p, soldOutItems) && productMatchesTagFilters(p, selectedTags)
          );
          const subcategoriesWithProducts = cat.subCategories?.filter((subCat) =>
            filteredProducts.some((p) => p.subCategoryName === subCat.name)
          ) || [];
          return { ...cat, products: filteredProducts, subCategories: subcategoriesWithProducts };
        })
        .filter((cat) => cat.products.length > 0)
        .filter((cat) => !isOptionSetCategory(cat))
    : [];

  // Fill missing category images with the first product's image as a fallback.
  const enrichedKioskRailCategories = kioskRailCategories.map((cat) => ({
    ...cat,
    categoryImage:
      cat.categoryImage ||
      cat.products?.[0]?.imageUrl ||
      cat.products?.[0]?.productDefaultImage ||
      "",
  }));

  const kioskAllCategory = kioskMode ? buildAllCategory(enrichedKioskRailCategories) : null;
  const kioskCategories = kioskAllCategory
    ? [kioskAllCategory, ...enrichedKioskRailCategories]
    : enrichedKioskRailCategories;

  // Search results panel: diet + search text combined, independent of the
  // rail selection above — only this feeds the right-hand results.
  const kioskSearchCategories = kioskMode
    ? filteredCategories.filter((cat) => !isOptionSetCategory(cat))
    : [];

  if (loading) {
    return (
      <div className="loader-container">
        <div className="coffee-loader"></div>
        <p key={loadingPhrase} className="loading-text-animated">
          {LOADING_PHRASES[loadingPhrase]}
        </p>
      </div>
    );
  }

  // Maintenance mode: when the backend reports it AND this is the billing QR
  // flow (?allowBilling=true), take over the whole screen like the mobile app.
  // The regular (non-billing) menu is never blocked by this.
  if (allowBilling && maintenance.enabled) {
    return (
      <MaintenanceScreen
        title={maintenance.title}
        message={maintenance.message}
        onRetry={() => window.location.reload()}
      />
    );
  }

  // Search Mode View (kiosk mode handles search inline within its own shell)
  if (searchMode && !kioskMode) {
    return (
      <div className={`search-mode-container ${searchClosing ? 'closing' : ''}`}>
        <header className="search-mode-header">
          <div className="search-mode-header-inner">
            <button
              type="button"
              className="back-button"
              onClick={handleBackFromSearch}
              aria-label="Back to menu"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              <span>Menu</span>
            </button>
            <div className="search-mode-bar-container">
              <span className="search-mode-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="6.5" />
                  <path d="m16 16 4 4" />
                </svg>
              </span>
              <label className="visually-hidden" htmlFor="menu-search-input">
                Search the menu
              </label>
              <input
                ref={searchInputRef}
                id="menu-search-input"
                type="search"
                inputMode="search"
                placeholder="Search coffee, food and more"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-mode-bar"
                autoComplete="off"
                autoFocus
              />
              {searchTerm && (
                <button
                  type="button"
                  className="clear-search-btn-mode"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m8 8 8 8M16 8l-8 8" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          {(vegTag || nonVegTag) && (
            <div className="search-diet-filters" aria-label="Diet filters">
              {vegTag && (
                <button
                  type="button"
                  className={`search-diet-chip veg ${selectedTags.includes(vegTag.id) ? "active" : ""}`}
                  onClick={() => handleTagToggle(vegTag.id)}
                  aria-pressed={selectedTags.includes(vegTag.id)}
                >
                  <span className="search-diet-mark" aria-hidden="true" />
                  <span>{vegTag.name || "Veg"}</span>
                </button>
              )}
              {nonVegTag && (
                <button
                  type="button"
                  className={`search-diet-chip non-veg ${selectedTags.includes(nonVegTag.id) ? "active" : ""}`}
                  onClick={() => handleTagToggle(nonVegTag.id)}
                  aria-pressed={selectedTags.includes(nonVegTag.id)}
                >
                  <span className="search-diet-mark" aria-hidden="true" />
                  <span>{nonVegTag.name || "Non-Veg"}</span>
                </button>
              )}
              {activeDietTag && (
                <button
                  type="button"
                  className="search-diet-clear"
                  onClick={() => setSelectedTags((prev) => prev.filter((id) => id !== activeDietTag.id))}
                >
                  Clear
                </button>
              )}
            </div>
          )}
          <p className="search-result-summary" aria-live="polite" aria-atomic="true">
            {trimmedSearchTerm
              ? `${searchResultCount} ${searchResultCount === 1 ? "item" : "items"} found for “${trimmedSearchTerm}”${activeDietName ? ` in ${activeDietName}` : ""}`
              : activeDietName ? `Search our ${activeDietName} menu` : "Search our menu"}
          </p>
        </header>

        <main className="search-results-container">
          {!trimmedSearchTerm ? (
            <section className="search-welcome-state" aria-labelledby="search-welcome-title">
              <div className="search-state-illustration" aria-hidden="true">
                <svg viewBox="0 0 64 64">
                  <circle cx="28" cy="28" r="17" />
                  <path d="m41 41 13 13" />
                  <path className="search-state-steam" d="M22 24c2-3 6-3 8 0" />
                </svg>
              </div>
              <p className="search-state-eyebrow">Find your next favourite</p>
              <h1 id="search-welcome-title">What are you craving?</h1>
              <p className="search-state-copy">
                {activeDietName
                  ? `Search is filtered to ${activeDietName} items. Pick a category below or type what you want.`
                  : "Search by drink, dish, ingredient, or explore a category below."}
              </p>
              {searchSuggestions.length > 0 && (
                <div className="search-suggestions" aria-label="Popular menu categories">
                  {searchSuggestions.map((category) => (
                    <button
                      type="button"
                      key={category.id}
                      onClick={() => {
                        setSearchTerm(category.parentName);
                        searchInputRef.current?.focus();
                      }}
                    >
                      {category.parentName}
                    </button>
                  ))}
                </div>
              )}
            </section>
          ) : filteredCategories.length > 0 ? (
            <div className="search-results-list">
              {filteredCategories.map((cat) => (
                <div key={cat.id} className="search-result-category">
                <CategorySection
                  category={cat}
                  tags={tags}
                  onProductClick={handleProductClick}
                  onAddToCart={isCartEnabled ? handleAddToCart : null}
                  onDecreaseFromCart={isCartEnabled ? handleDecreaseFromCart : null}
                  getProductCartQty={isCartEnabled ? getProductCartQty : null}
                  getProductCartLineCount={isCartEnabled ? getProductCartLineCount : null}
                  subcategoryRefs={subcategoryRefs}
                  highlightText={highlightText}
                  searchTerm={searchTerm}
                  soldOutItems={soldOutItems}
                  isCartEnabled={isCartEnabled}
                />
                </div>
              ))}
            </div>
          ) : (
            <section className="search-empty-state" aria-labelledby="search-empty-title">
              <div className="search-state-illustration search-state-illustration-empty" aria-hidden="true">
                <svg viewBox="0 0 64 64">
                  <circle cx="28" cy="28" r="17" />
                  <path d="m41 41 13 13" />
                  <path d="M22 28h12" />
                </svg>
              </div>
              <p className="search-state-eyebrow">No matches yet</p>
              <h1 id="search-empty-title">We couldn’t find “{trimmedSearchTerm}”</h1>
              <p className="search-state-copy">
                {activeDietName
                  ? `No ${activeDietName} item matches this search. Try another name or clear the diet filter.`
                  : "Try a shorter name, a different spelling, or search for a category such as coffee or food."}
              </p>
              <div className="search-empty-actions">
                <button type="button" className="search-primary-action" onClick={handleClearSearch}>
                  Clear search
                </button>
                {activeDietTag && (
                  <button
                    type="button"
                    className="search-secondary-action"
                    onClick={() => setSelectedTags((prev) => prev.filter((id) => id !== activeDietTag.id))}
                  >
                    Clear {activeDietName}
                  </button>
                )}
                <button type="button" className="search-secondary-action" onClick={handleBackFromSearch}>
                  Back to menu
                </button>
              </div>
            </section>
          )}
        </main>

        {selectedProduct && (
          <ProductDetailModal
            product={selectedProduct}
            onClose={closeModal}
            soldOutItems={soldOutItems}
            onAddToCart={isCartEnabled ? handleAddToCart : null}
            onReplaceCartItem={isCartEnabled ? cart?.replaceCartItem : null}
            compactMode={openMode === 'add'}
            initialCartItem={initialCartItem}
          />
        )}

        {isCartEnabled && customizationProduct && (
          <CustomizationChooserSheet
            product={customizationProduct}
            cartItems={cart?.items}
            cafeId={selectedCafe?.ristaId || selectedCafe?.cafeCode}
            cafeName={branchName || store?.name || selectedCafe?.name}
            onClose={() => setCustomizationProduct(null)}
            onAddNew={handleAddNewCustomization}
            onEdit={handleEditCustomization}
            onUpdateQuantity={cart?.updateQuantity}
          />
        )}

        {isCartEnabled && (
          <CartSheet
            onViewCart={() => {
              trackCartViewed({
                cafeId: selectedCafe?.ristaId || selectedCafe?.cafeCode,
                cafeName: branchName || store?.name || selectedCafe?.name,
                source: "cart_sheet",
                items: cart?.items,
                itemCount: cart?.itemCount,
                lineCount: cart?.items?.length,
                subtotal: cart?.subtotal,
                taxTotal: cart?.taxTotal,
                grandTotal: cart?.grandTotal,
                hasTableNumber: Boolean(cart?.tableNumber),
                hasPhoneNumber: Boolean(cart?.phoneNumber),
              });
              setShowCartPage(true);
            }}
          />
        )}
        {isCartEnabled && <CartExpiryToast />}

        {isCartEnabled && showCartPage && (
          <CartPageView
            cafeId={selectedCafe?.ristaId || selectedCafe?.cafeCode}
            cafeName={branchName || store?.name || selectedCafe?.name}
            onBack={() => {
              trackCartClosed({
                cafeId: selectedCafe?.ristaId || selectedCafe?.cafeCode,
                cafeName: branchName || store?.name || selectedCafe?.name,
                source: "cart_page",
                itemCount: cart?.itemCount,
                lineCount: cart?.items?.length,
                subtotal: cart?.subtotal,
                taxTotal: cart?.taxTotal,
                grandTotal: cart?.grandTotal,
                hasTableNumber: Boolean(cart?.tableNumber),
                hasPhoneNumber: Boolean(cart?.phoneNumber),
              });
              setShowCartPage(false);
            }}
            onEditItem={handleEditCartItem}
            onViewSummary={() => { setShowCartPage(false); setShowOrderSummary(true); }}
            onOrderDone={() => { setShowCartPage(false); }}
          />
        )}

        {isCartEnabled && showOrderSummary && (
          <OrderSummaryView
            cafeId={selectedCafe?.ristaId || selectedCafe?.cafeCode}
            cafeName={branchName || store?.name}
            onEditOrder={() => { setShowOrderSummary(false); setShowCartPage(true); }}
            onOrderDone={() => { cart?.clearCart({ clearSource: "order_summary_done" }); setShowOrderSummary(false); }}
          />
        )}
      </div>
    );
  }

  // Get cafe name for display - prioritize branch name from IndexedDB, then store from API response
  const getCafeName = () => {
    // Use branch name from IndexedDB (fetched via branchCode)
    if (branchName) {
      return branchName;
    }
    // Use store name from menu API response if available
    if (store?.name) {
      return store.name;
    }
    // Fall back to default
    return 'Blue Tokai - Location';
  };

  // Normal View
  return (
    <div className={`menu-container ${kioskMode ? "kiosk-mode" : ""} ${isCartEnabled && cart?.itemCount > 0 ? "has-cart-bar" : ""}`}>
      {kioskMode && (
        <KioskMenu
          cafeName={getCafeName()}
          categories={kioskCategories}
          tags={tags}
          selectedTags={selectedTags}
          onTagToggle={handleTagToggle}
          onProductClick={handleProductClick}
          onAddToCart={isCartEnabled ? handleAddToCart : null}
          onDecreaseFromCart={isCartEnabled ? handleDecreaseFromCart : null}
          getProductCartQty={isCartEnabled ? getProductCartQty : null}
          getProductCartLineCount={isCartEnabled ? getProductCartLineCount : null}
          soldOutItems={soldOutItems}
          isCartEnabled={isCartEnabled}
          billing={billingAllowed}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          highlightText={highlightText}
          searchCategories={kioskSearchCategories}
          cartItemCount={cart?.itemCount || 0}
          banners={banners}
          onBannerClick={handleBannerClick}
          showCafeSelector={showCafeSelector}
          onSelectCafe={onClearCafe}
          onViewCart={() => {
            trackCartViewed({
              cafeId: selectedCafe?.ristaId || selectedCafe?.cafeCode,
              cafeName: branchName || store?.name || selectedCafe?.name,
              source: "kiosk_top_bar",
              items: cart?.items,
              itemCount: cart?.itemCount,
              lineCount: cart?.items?.length,
              subtotal: cart?.subtotal,
              taxTotal: cart?.taxTotal,
              grandTotal: cart?.grandTotal,
              hasTableNumber: Boolean(cart?.tableNumber),
              hasPhoneNumber: Boolean(cart?.phoneNumber),
            });
            setShowCartPage(true);
          }}
        />
      )}

      {!kioskMode && (
        <>
      {/* Background Art - positioned behind everything */}
      <img
        src="/BTCArtSmall.png"
        alt=""
        className="header-background-art"
        aria-hidden="true"
      />

      <div className="menu-header">
        <div className="menu-left">
          {/* Back to cafes button (optional) */}
          {onBackToCafes && (
            <button className="back-to-cafes-btn" onClick={handleBackToCafes}>
              ←
            </button>
          )}
          <div className="menu-logo-title">
            <img
              src="/BTC_Blue_Paradise.png"
              alt="Blue Tokai Logo"
              className="menu-logo"
            />
            <div className="menu-text">
              <h1>Blue Tokai</h1>
              <p className="menu-subtitle">{getCafeName()}</p>
            </div>
          </div>
        </div>
        {showCafeSelector && (
          <div className="menu-right">
            <button className="select-cafe-btn" onClick={onClearCafe}>
              Select Café
            </button>
          </div>
        )}
      </div>

      {/* Search Bar (hidden on pay-from-the-menu links) */}
      {!billingAllowed && (
      <div
        ref={searchBarRef}
        className={`search-bar-container ${isSearchSticky ? "sticky" : ""}`}
        onClick={handleSearchClick}
      >
        <div className="search-bar">
          <span key={searchPlaceholder} className="search-text-animated">
            {searchPlaceholders[searchPlaceholder]}
          </span>
        </div>
      </div>
      )}

      {isSearchSticky && <div className="search-spacer"></div>}

      {/* Banner Section (hidden on pay-from-the-menu links) */}
      {!billingAllowed && banners.length > 0 && (
        <div className="banner-section">
          <Swiper
             slidesPerView={'auto'}
             modules={[Autoplay, A11y]}
             loop
             autoplay={{ delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }}
             pagination={{ clickable: true }}
             centeredSlides={true}
             spaceBetween={12}
             grabCursor
             className="banner-swiper"
             breakpoints={{
               700: {
                 slidesPerView: 2,
                 spaceBetween:22,
                 centeredSlides: false,
               },
               1024: {
                 slidesPerView: 3,
                 spaceBetween: 20,
                 centeredSlides: false,
               },
             }}
          >
            {banners.map((banner) => (
              <SwiperSlide
                key={banner.id}
                data-swiper-autoplay={(banner.duration || 5) * 1000}
              >
                <div
                  className="banner-card"
                  onClick={() => handleBannerClick(banner)}
                  style={{ cursor: (banner.redirectMode === 'product' || banner.redirectMode === 'category') ? 'pointer' : 'default' }}
                >
                  {banner.mediaType === "video" ? (
                    <>
                      {!loadedVideos[banner.id] && (
                        <div className="banner-video-skeleton">
                          <div className="skeleton-shimmer"></div>
                        </div>
                      )}
                      <CachedVideo
                        className="banner-video"
                        src={banner.mediaUrl}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                        onLoadedData={() => handleVideoLoaded(banner.id)}
                        style={{
                          opacity: loadedVideos[banner.id] ? 1 : 0,
                          visibility: loadedVideos[banner.id] ? 'visible' : 'hidden'
                        }}
                      />
                    </>
                  ) : (
                    <CachedImage
                      src={banner.mediaUrl}
                      alt={banner.title || banner.alt || "Promotional banner"}
                      className="banner-image"
                    />
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {/* Dynamic Tag Filters */}
      {tags.length > 0 && (() => {
        // Separate veg/non-veg tags from other tags
        const otherTags = tags.filter(t => {
          const name = t.name?.toLowerCase() || '';
          return !(name.includes('veg'));
        });

        return (
          <>
            <div
              ref={filterBarRef}
              className={`tag-filters-container ${isFilterSticky ? 'sticky' : ''}`}
              style={isFilterSticky ? { top: searchBarRef.current?.offsetHeight || 0 } : {}}
            >
              <div className="tag-filters-scroll">
                {/* Veg Toggle Switch - only show if vegTag exists */}
                {vegTag && (
                  <div className="diet-toggle-container diet-toggle-single">
                    {selectedTags.includes(vegTag.id) && (
                      <button
                        className="diet-clear-btn"
                        onClick={() => {
                          setSelectedTags(prev => prev.filter(id => id !== vegTag.id));
                        }}
                        title="Clear filter"
                      >
                        ✕
                      </button>
                    )}
                    <button
                      className={`diet-toggle-option ${selectedTags.includes(vegTag.id) ? 'active' : ''}`}
                      onClick={() => handleTagToggle(vegTag.id)}
                    >
                      {vegTag.categoryImage && (
                        <img
                          src={vegTag.categoryImage}
                          alt={vegTag.name}
                          className="diet-icon"
                        />
                      )}
                      <span className="diet-label">{vegTag.name || 'Veg'}</span>
                    </button>
                  </div>
                )}

                {/* Non-Veg Toggle Switch - only show if nonVegTag exists */}
                {nonVegTag && (
                  <div className="diet-toggle-container diet-toggle-single">
                    {selectedTags.includes(nonVegTag.id) && (
                      <button
                        className="diet-clear-btn"
                        onClick={() => {
                          setSelectedTags(prev => prev.filter(id => id !== nonVegTag.id));
                        }}
                        title="Clear filter"
                      >
                        ✕
                      </button>
                    )}
                    <button
                      className={`diet-toggle-option ${selectedTags.includes(nonVegTag.id) ? 'active' : ''}`}
                      onClick={() => handleTagToggle(nonVegTag.id)}
                    >
                      {nonVegTag.categoryImage && (
                        <img
                          src={nonVegTag.categoryImage}
                          alt={nonVegTag.name}
                          className="diet-icon"
                        />
                      )}
                      <span className="diet-label">{nonVegTag.name || 'Non-Veg'}</span>
                    </button>
                  </div>
                )}

                {/* Other Tag Filters */}
                {otherTags.map((tag) => {
                  const isSelected = selectedTags.includes(tag.id);
                  const isSpecialTag = tag.id === '6932636dec4624b7afca0266';
                  // Use white stars image when special tag is selected, otherwise use default image
                  const imageSrc = isSpecialTag && isSelected 
                    ? 'https://cdn.bluetokaicoffee.com/tags/WhiteStars.png'
                    : tag.categoryImage;
                  
                  return (
                    <button
                      key={tag.id}
                      className={`tag-filter-btn ${isSelected ? 'active' : ''}`}
                      onClick={() => handleTagToggle(tag.id)}
                    >
                      {imageSrc && (
                        <img
                          src={imageSrc}
                          alt={tag.name}
                          className="tag-icon"
                        />
                      )}
                      <span className="tag-name">{tag.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {isFilterSticky && <div className="filter-spacer"></div>}
          </>
        );
      })()}
      <hr/>
      <div className="main-menu-container">
      {/* Categories */}
      {filteredCategories.length > 0 ? (
        filteredCategories.map((cat) => (
          <div key={cat.id} ref={(el) => (sectionRefs.current[cat.id] = el)}>
            <CategorySection
              category={cat}
              tags={tags}
              onProductClick={handleProductClick}
              onAddToCart={isCartEnabled ? handleAddToCart : null}
              onDecreaseFromCart={isCartEnabled ? handleDecreaseFromCart : null}
              getProductCartQty={isCartEnabled ? getProductCartQty : null}
              getProductCartLineCount={isCartEnabled ? getProductCartLineCount : null}
              subcategoryRefs={subcategoryRefs}
              highlightText={highlightText}
              searchTerm={searchTerm}
              soldOutItems={soldOutItems}
              isCartEnabled={isCartEnabled}
            />
          </div>
        ))
      ) : (
        <p className="no-products-message">No products found</p>
      )}
      </div>

      {/* Floating Menu - hide when overlay is open */}
      {!mobileMenuOpen && (
        <button
          className={`floating-menu-btn ${(isCartEnabled && cart?.itemCount > 0) ? "with-cart-bar" : ""}`}
          onClick={() => setMobileMenuOpen(true)}
        >
          <div className="floating-menu-content">
            <span>☰</span>
            <span className="floating-menu-label">Menu</span>
          </div>
        </button>
      )}

      {mobileMenuOpen && (
        <>
          <div className="fullscreen-menu" onClick={() => setMobileMenuOpen(false)}>
            <div className="fullscreen-menu-content" onClick={(e) => e.stopPropagation()}>
              {filteredCategories.map((cat) => (
                <div key={cat.id} className="menu-category-item">
                  <button
                    className="menu-category-btn"
                    onClick={() => {
                      scrollToCategory(cat.id);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <span className="category-name">{cat.parentName || "Unnamed Category"}</span>
                    {cat.subCategories && cat.subCategories.length > 0 && (
                      <span className="accordion-icon" onClick={(e) => {
                        e.stopPropagation();
                        toggleCategory(cat.id);
                      }}>
                        {expandedCategories[cat.id] ? '−' : '+'}
                      </span>
                    )}
                  </button>
                  {expandedCategories[cat.id] && cat.subCategories && cat.subCategories.length > 0 && (
                    <div className="subcategories-list">
                      {cat.subCategories.map((subCat) => (
                        <button
                          key={`${cat.id}-${subCat.name}`}
                          className="menu-subcategory-btn"
                          onClick={() => {
                            scrollToCategory(cat.id, subCat.name);
                            setMobileMenuOpen(false);
                          }}
                        >
                          {subCat.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <button className="close-btn-bottom" onClick={() => setMobileMenuOpen(false)}>
            ✕
          </button>
        </>
      )}
        </>
      )}

      {selectedProduct && (
          <ProductDetailModal
            product={selectedProduct}
            onClose={closeModal}
            soldOutItems={soldOutItems}
            onAddToCart={isCartEnabled ? handleAddToCart : null}
            onReplaceCartItem={isCartEnabled ? cart?.replaceCartItem : null}
            compactMode={openMode === 'add'}
            initialCartItem={initialCartItem}
          />
      )}

      {isCartEnabled && customizationProduct && (
        <CustomizationChooserSheet
          product={customizationProduct}
          cartItems={cart?.items}
          cafeId={selectedCafe?.ristaId || selectedCafe?.cafeCode}
          cafeName={branchName || store?.name || selectedCafe?.name}
          onClose={() => setCustomizationProduct(null)}
          onAddNew={handleAddNewCustomization}
          onEdit={handleEditCustomization}
          onUpdateQuantity={cart?.updateQuantity}
        />
      )}

      {isCartEnabled && (
        <CartSheet
          onViewCart={() => {
            trackCartViewed({
              cafeId: selectedCafe?.ristaId || selectedCafe?.cafeCode,
              cafeName: branchName || store?.name || selectedCafe?.name,
              source: "cart_sheet",
              items: cart?.items,
              itemCount: cart?.itemCount,
              lineCount: cart?.items?.length,
              subtotal: cart?.subtotal,
              taxTotal: cart?.taxTotal,
              grandTotal: cart?.grandTotal,
              hasTableNumber: Boolean(cart?.tableNumber),
              hasPhoneNumber: Boolean(cart?.phoneNumber),
            });
            setShowCartPage(true);
          }}
        />
      )}
        {isCartEnabled && <CartExpiryToast />}

      {isCartEnabled && showCartPage && (
        <CartPageView
          cafeId={selectedCafe?.ristaId || selectedCafe?.cafeCode}
          cafeName={branchName || store?.name || selectedCafe?.name}
          onBack={() => {
            trackCartClosed({
              cafeId: selectedCafe?.ristaId || selectedCafe?.cafeCode,
              cafeName: branchName || store?.name || selectedCafe?.name,
              source: "cart_page",
              itemCount: cart?.itemCount,
              lineCount: cart?.items?.length,
              subtotal: cart?.subtotal,
              taxTotal: cart?.taxTotal,
              grandTotal: cart?.grandTotal,
              hasTableNumber: Boolean(cart?.tableNumber),
              hasPhoneNumber: Boolean(cart?.phoneNumber),
            });
            setShowCartPage(false);
          }}
          onEditItem={handleEditCartItem}
          onViewSummary={() => { setShowCartPage(false); setShowOrderSummary(true); }}
        />
      )}

      {isCartEnabled && showOrderSummary && (
        <OrderSummaryView
          cafeId={selectedCafe?.ristaId || selectedCafe?.cafeCode}
          cafeName={branchName || store?.name}
          onEditOrder={() => { setShowOrderSummary(false); setShowCartPage(true); }}
          onOrderDone={() => { cart?.clearCart({ clearSource: "order_summary_done" }); setShowOrderSummary(false); }}
        />
      )}

      {showScrollTop && !mobileMenuOpen && (
        <div className="scroll-to-top" onClick={scrollToTop}>
          ↑
        </div>
      )}
    </div>
  );
}
